import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, RefreshCw, AlertTriangle, X, Copy, Eye, EyeOff, ArrowRight, ChevronDown, Loader2 } from 'lucide-react';
import { useProjects } from '../hooks/useProjects';
import { useToast } from '../components/ui/toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { apiClient } from '../api/client';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

interface SecretValue {
  value: string;
  exists: boolean;
}

interface ComparisonMatrix {
  [key: string]: {
    [envName: string]: SecretValue;
  };
}

interface ExportResponse {
  secrets: Array<{
    key: string;
    value: string;
  }>;
}

const MASKED_VALUE = '••••••••';

function MismatchIcon() {
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold">
      ≠
    </span>
  );
}

export function EnvironmentComparisonPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const { selectedProject, selectedProjectId, selectProject, environments } = useProjects();

  useEffect(() => {
    if (projectId && projectId !== selectedProjectId) {
      selectProject(projectId);
    }
  }, [projectId, selectedProjectId, selectProject]);

  const actualProjectId = projectId || selectedProject?.id || null;

  const [matrix, setMatrix] = useState<ComparisonMatrix>({});
  const [isLoadingMatrix, setIsLoadingMatrix] = useState(false);
  const [allKeys, setAllKeys] = useState<string[]>([]);
  const [revealedCells, setRevealedCells] = useState<Set<string>>(new Set());
  const [promotingCell, setPromotingCell] = useState<string | null>(null);

  const getCellKey = (secretKey: string, envName: string) => `${secretKey}::${envName}`;

  const toggleReveal = useCallback((secretKey: string, envName: string) => {
    const cellKey = getCellKey(secretKey, envName);
    setRevealedCells(prev => {
      const next = new Set(prev);
      if (next.has(cellKey)) {
        next.delete(cellKey);
      } else {
        next.add(cellKey);
      }
      return next;
    });
  }, []);

  const revealAll = useCallback(() => {
    const allCellKeys = new Set<string>();
    allKeys.forEach(key => {
      environments.forEach(env => {
        if (matrix[key]?.[env.name]?.exists) {
          allCellKeys.add(getCellKey(key, env.name));
        }
      });
    });
    setRevealedCells(allCellKeys);
  }, [allKeys, environments, matrix]);

  const hideAll = useCallback(() => {
    setRevealedCells(new Set());
  }, []);

  const promoteSecret = useCallback(async (
    secretKey: string,
    sourceEnv: string,
    targetEnv: string
  ) => {
    if (!actualProjectId) return;

    const cellKey = getCellKey(secretKey, sourceEnv);
    setPromotingCell(cellKey);

    try {
      const sourceValue = matrix[secretKey]?.[sourceEnv]?.value;
      if (!sourceValue) {
        toast.error('Source value not found');
        return;
      }

      const targetExists = matrix[secretKey]?.[targetEnv]?.exists;

      if (targetExists) {
        await apiClient.put(
          `/api/v1/projects/${actualProjectId}/environments/${targetEnv}/secrets/${secretKey}`,
          { value: sourceValue }
        );
      } else {
        await apiClient.post(
          `/api/v1/projects/${actualProjectId}/environments/${targetEnv}/secrets`,
          { key: secretKey, value: sourceValue }
        );
      }

      setMatrix(prev => ({
        ...prev,
        [secretKey]: {
          ...prev[secretKey],
          [targetEnv]: { value: sourceValue, exists: true }
        }
      }));

      toast.success(`Promoted "${secretKey}" to ${targetEnv}`);
    } catch (error) {
      console.error('Failed to promote secret:', error);
      toast.error(`Failed to promote to ${targetEnv}`);
    } finally {
      setPromotingCell(null);
    }
  }, [actualProjectId, matrix, toast]);

  const loadMatrix = useCallback(async () => {
    if (!actualProjectId || environments.length === 0) return;

    setIsLoadingMatrix(true);
    setRevealedCells(new Set());
    try {
      const promises = environments.map(env =>
        apiClient.get<ExportResponse>(`/api/v1/projects/${actualProjectId}/environments/${env.name}/secrets/export`)
          .then(res => ({ envName: env.name, secrets: res.data.secrets }))
          .catch(err => {
            console.error(`Failed to fetch secrets for ${env.name}`, err);
            return { envName: env.name, secrets: [] };
          })
      );

      const results = await Promise.all(promises);

      const newMatrix: ComparisonMatrix = {};
      const keysSet = new Set<string>();

      results.forEach(({ envName, secrets }) => {
        secrets.forEach(secret => {
          keysSet.add(secret.key);
          if (!newMatrix[secret.key]) {
            newMatrix[secret.key] = {};
          }
          newMatrix[secret.key][envName] = {
            value: secret.value,
            exists: true
          };
        });
      });

      keysSet.forEach(key => {
        if (!newMatrix[key]) newMatrix[key] = {};
        environments.forEach(env => {
          if (!newMatrix[key][env.name]) {
            newMatrix[key][env.name] = {
              value: '',
              exists: false
            };
          }
        });
      });

      setMatrix(newMatrix);
      setAllKeys(Array.from(keysSet).sort());
    } catch (error) {
      console.error('Failed to load comparison matrix:', error);
      toast.error('Failed to load comparison matrix');
    } finally {
      setIsLoadingMatrix(false);
    }
  }, [actualProjectId, environments, toast]);

  useEffect(() => {
    if (actualProjectId && environments.length > 0) {
      loadMatrix();
    }
  }, [actualProjectId, environments.length, loadMatrix]);

  const stats = useMemo(() => {
    let totalMissing = 0;
    let totalMismatch = 0;

    allKeys.forEach(key => {
      const values = environments.map(env => matrix[key]?.[env.name]?.value);
      const exists = environments.map(env => matrix[key]?.[env.name]?.exists);

      if (exists.some(e => !e)) {
        totalMissing++;
      }

      if (exists.every(e => e)) {
        const firstVal = values[0];
        if (values.some(v => v !== firstVal)) {
          totalMismatch++;
        }
      }
    });

    return { totalMissing, totalMismatch };
  }, [matrix, allKeys, environments]);

  const getRowStatus = useCallback((key: string) => {
    const rowValues = environments.map(env => matrix[key]?.[env.name]);
    const allExist = rowValues.every(v => v?.exists);
    const hasMismatch = allExist && rowValues.some(v => v?.value !== rowValues[0]?.value);
    const hasMissing = rowValues.some(v => !v?.exists);
    return { allExist, hasMismatch, hasMissing };
  }, [environments, matrix]);

  const getCellMismatch = useCallback((key: string, envName: string) => {
    const rowValues = environments.map(env => matrix[key]?.[env.name]);
    const allExist = rowValues.every(v => v?.exists);
    if (!allExist) return false;

    const currentValue = matrix[key]?.[envName]?.value;
    return rowValues.some(v => v?.value !== currentValue);
  }, [environments, matrix]);

  if (!actualProjectId) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">
              Select a project from the sidebar to compare environments.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="h-full flex flex-col space-y-6 p-6"
    >
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/projects/${actualProjectId}/secrets`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Comparison Matrix</h1>
            <p className="text-sm text-muted-foreground">
              Compare secret values across all environments
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-2">
            {stats.totalMissing > 0 && (
              <Badge variant="destructive" className="h-7 gap-1.5">
                <X className="h-3 w-3" />
                {stats.totalMissing} Missing
              </Badge>
            )}
            {stats.totalMismatch > 0 && (
              <Badge variant="secondary" className="h-7 gap-1.5 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border-amber-500/20">
                <MismatchIcon />
                {stats.totalMismatch} Mismatches
              </Badge>
            )}
          </div>

          <div className="h-6 w-px bg-border" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5">
                <Eye className="h-4 w-4" />
                Visibility
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={revealAll}>
                <Eye className="h-4 w-4 mr-2" />
                Reveal all values
              </DropdownMenuItem>
              <DropdownMenuItem onClick={hideAll}>
                <EyeOff className="h-4 w-4 mr-2" />
                Hide all values
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            variant="outline"
            size="sm"
            onClick={loadMatrix}
            disabled={isLoadingMatrix}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingMatrix ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="flex-1"
      >
        <Card className="h-full overflow-hidden border-border bg-card/50">
        <ScrollArea className="h-[calc(100vh-220px)]">
          <div>
            <Table>
              <TableHeader className="sticky top-0 z-20">
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="w-[200px] min-w-[200px] font-mono text-xs uppercase tracking-wider text-muted-foreground bg-background sticky left-0 z-30 border-r border-border">
                    <div className="flex items-center gap-2">
                      Secret Key
                    </div>
                  </TableHead>
                  {environments.map(env => (
                    <TableHead key={env.id} className="w-[220px] min-w-[180px] font-mono text-xs uppercase tracking-wider text-muted-foreground bg-background">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-success/70" />
                        {env.name}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingMatrix ? (
                  <TableRow>
                    <TableCell colSpan={environments.length + 1} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-3 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                        <span>Loading comparison data...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : allKeys.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={environments.length + 1} className="h-32 text-center">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <AlertTriangle className="h-6 w-6 opacity-50" />
                        <span>No secrets found in any environment</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  allKeys.map(key => {
                    const { hasMismatch, hasMissing } = getRowStatus(key);

                    return (
                      <TableRow
                        key={key}
                        className={`
                          border-border transition-colors
                          ${hasMissing ? 'bg-destructive/[0.02]' : ''}
                          ${hasMismatch && !hasMissing ? 'bg-warning/[0.02]' : ''}
                          hover:bg-accent/30
                        `}
                      >
                        <TableCell className="font-mono text-sm font-medium text-foreground bg-background/80 sticky left-0 z-10 border-r border-border/50">
                          <div className="flex items-center gap-2 pr-2">
                            <span className="truncate max-w-[140px]">{key}</span>
                            {hasMissing && (
                              <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-red-500/30 text-red-400 bg-red-500/10">
                                <X className="h-2.5 w-2.5 mr-0.5" />
                                MISSING
                              </Badge>
                            )}
                            {hasMismatch && !hasMissing && (
                              <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-amber-500/30 text-amber-400 bg-amber-500/10">
                                <MismatchIcon />
                                <span className="ml-1">DIFFERS</span>
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {environments.map(env => {
                          const cellData = matrix[key]?.[env.name];
                          const isMissing = !cellData?.exists;
                          const cellKey = getCellKey(key, env.name);
                          const isRevealed = revealedCells.has(cellKey);
                          const isPromoting = promotingCell === cellKey;
                          const isMismatched = getCellMismatch(key, env.name);
                          const otherEnvs = environments.filter(e => e.name !== env.name);

                          return (
                            <TableCell key={env.id} className="p-0">
                              <div className={`
                                min-h-[52px] w-full px-4 py-3 border-l border-border/30 transition-colors
                                ${isMissing
                                  ? 'bg-destructive/5'
                                  : isMismatched
                                    ? 'bg-warning/[0.03]'
                                    : 'hover:bg-accent/20'
                                }
                              `}>
                                {isMissing ? (
                                  <div className="flex items-center justify-between group/missing">
                                    <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-destructive/70">
                                      <div className="w-5 h-5 rounded border border-dashed border-destructive/30 flex items-center justify-center">
                                        <X className="h-3 w-3" />
                                      </div>
                                      Not defined
                                    </span>
                                    {otherEnvs.some(e => matrix[key]?.[e.name]?.exists) && (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground opacity-0 group-hover/missing:opacity-100 transition-opacity"
                                          >
                                            <ArrowRight className="h-3 w-3 mr-1" />
                                            Copy from
                                            <ChevronDown className="h-3 w-3 ml-1 opacity-50" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuLabel className="text-xs text-muted-foreground">
                                            Copy value from
                                          </DropdownMenuLabel>
                                          <DropdownMenuSeparator />
                                          {otherEnvs
                                            .filter(e => matrix[key]?.[e.name]?.exists)
                                            .map(sourceEnv => (
                                              <DropdownMenuItem
                                                key={sourceEnv.id}
                                                onClick={() => promoteSecret(key, sourceEnv.name, env.name)}
                                              >
                                                <ArrowRight className="h-3 w-3 mr-2" />
                                                {sourceEnv.name}
                                              </DropdownMenuItem>
                                            ))
                                          }
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between gap-3 group/cell">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      {isMismatched && <MismatchIcon />}
                                      <button
                                        onClick={() => toggleReveal(key, env.name)}
                                        className="font-mono text-sm text-muted-foreground hover:text-foreground truncate max-w-[100px] text-left transition-colors"
                                        title={isRevealed ? cellData.value : 'Click to reveal'}
                                      >
                                        {isRevealed ? cellData.value : MASKED_VALUE}
                                      </button>
                                    </div>

                                    <div className="flex items-center gap-1 opacity-0 group-hover/cell:opacity-100 transition-opacity">
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                        onClick={() => toggleReveal(key, env.name)}
                                        title={isRevealed ? 'Hide value' : 'Reveal value'}
                                      >
                                        {isRevealed ? (
                                          <EyeOff className="h-3.5 w-3.5" />
                                        ) : (
                                          <Eye className="h-3.5 w-3.5" />
                                        )}
                                      </Button>

                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                        onClick={() => {
                                          navigator.clipboard.writeText(cellData.value);
                                          toast.success('Copied to clipboard');
                                        }}
                                        title="Copy value"
                                      >
                                        <Copy className="h-3.5 w-3.5" />
                                      </Button>

                                      {otherEnvs.length > 0 && (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7 text-muted-foreground hover:text-success"
                                              disabled={isPromoting}
                                              title="Promote to other environment"
                                            >
                                              {isPromoting ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                              ) : (
                                                <ArrowRight className="h-3.5 w-3.5" />
                                              )}
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end">
                                            <DropdownMenuLabel className="text-xs text-muted-foreground">
                                              Promote to
                                            </DropdownMenuLabel>
                                            <DropdownMenuSeparator />
                                            {otherEnvs.map(targetEnv => (
                                              <DropdownMenuItem
                                                key={targetEnv.id}
                                                onClick={() => promoteSecret(key, env.name, targetEnv.name)}
                                                className="gap-2"
                                              >
                                                <ArrowRight className="h-3 w-3" />
                                                {targetEnv.name}
                                                {!matrix[key]?.[targetEnv.name]?.exists && (
                                                  <Badge variant="outline" className="ml-auto h-4 px-1 text-[9px] border-destructive/30 text-destructive">
                                                    NEW
                                                  </Badge>
                                                )}
                                                {matrix[key]?.[targetEnv.name]?.exists &&
                                                  matrix[key]?.[targetEnv.name]?.value !== cellData.value && (
                                                  <Badge variant="outline" className="ml-auto h-4 px-1 text-[9px] border-warning/30 text-warning">
                                                    OVERWRITE
                                                  </Badge>
                                                )}
                                              </DropdownMenuItem>
                                            ))}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
        </Card>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="flex items-center justify-between text-xs text-muted-foreground"
      >
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-success/70" />
            {environments.length} environment{environments.length !== 1 ? 's' : ''}
          </span>
          <span>{allKeys.length} secret{allKeys.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5">
            <X className="h-3 w-3 text-destructive" />
            Missing in some environments
          </span>
          <span className="flex items-center gap-1.5">
            <MismatchIcon />
            Values differ across environments
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}
